import {Card, FormControl} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useFieldArray, UseFormReturn, useWatch} from 'react-hook-form';

import {always, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {GetServiceCaseIntegrationsEvaClaimServiceOrder} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {DOT_CHARACTER} from 'shared/web';

import {EvaClaimFormSchemaType} from '../types/EvaClaimFormSchemaType';

interface ServiceOrderFieldsProps {
  control: FormControl<EvaClaimFormSchemaType>;
  formApi: UseFormReturn<EvaClaimFormSchemaType>;
  orders: GetServiceCaseIntegrationsEvaClaimServiceOrder[];
}

export function ServiceOrderFields(props: ServiceOrderFieldsProps) {
  const orderFields = useFieldArray<EvaClaimFormSchemaType, 'orders'>({
    control: props.control,
    name: 'orders',
  });

  const ordersFormValues = useWatch({
    control: props.control,
    name: 'orders',
  });

  return (
    <Box backgroundColor="general.separator" padding={4} borderRadius="small" flex={1}>
      <VStack spacing={4}>
        {orderFields.fields.map((field, index) => {
          const orderData = props.orders.find(
            (item) => item.serviceOrderId === field.serviceOrderId
          );

          const fieldValue = ordersFormValues?.[index]?.isSelected ?? false;
          const tooltipContent = getTooltipContent(orderData);
          return (
            <Card
              key={field.id}
              control={{
                value: fieldValue,
                onChange: () => props.formApi.setValue(`orders.${index}.isSelected`, !fieldValue),
                type: 'checkbox',
              }}
              hasSeparator={false}
              title={orderData?.number ?? ''}
              tooltip={tooltipContent}
            >
              <Text>{orderData?.variantName ?? ''}</Text>
            </Card>
          );
        })}
      </VStack>
    </Box>
  );
}

const getTooltipContent = (orderData?: GetServiceCaseIntegrationsEvaClaimServiceOrder) => {
  if (!orderData || !orderData.tooltip) {
    return null;
  }

  const hasClaimType = !isNil(orderData?.tooltip?.claimType);
  const hasDamageCode = !isNilOrEmpty(orderData?.tooltip?.damageCode);
  const hasEskulabAuthorizationNumber = !isNilOrEmpty(
    orderData?.tooltip?.eskulabAuthorizationNumber
  );
  const hasServiceRequests = !isNilOrEmpty(orderData?.tooltip?.serviceRequests);

  return (
    <VStack spacing={2}>
      <Show when={hasClaimType}>
        <VStack>
          <Text size="small" color="white">
            {i18n.t('entity.integration.labels.evaClaimTooltipType')}
          </Text>
          <Text size="small" color="white">
            {DOT_CHARACTER}{' '}
            {match(orderData?.tooltip?.claimType)
              .with(0, always(i18n.t('entity.vehicle.labels.vehicle')))
              .with(1, always(i18n.t('entity.claimType.labels.part')))
              .otherwise(always(undefined))}
          </Text>
        </VStack>
      </Show>
      <Show when={hasDamageCode}>
        <VStack>
          <Text size="small" color="white">
            {i18n.t('entity.integration.labels.evaClaimTooltipDamageCode')}
          </Text>
          <Text size="small" color="white">
            {DOT_CHARACTER} {orderData?.tooltip?.damageCode ?? ''}
          </Text>
        </VStack>
      </Show>
      <Show when={hasEskulabAuthorizationNumber}>
        <VStack>
          <Text size="small" color="white">
            {i18n.t('entity.integration.labels.evaClaimTooltipEskulabAuthorizationNumber')}
          </Text>
          <Text size="small" color="white">
            {DOT_CHARACTER} {orderData?.tooltip?.eskulabAuthorizationNumber ?? ''}
          </Text>
        </VStack>
      </Show>
      <Show when={hasServiceRequests}>
        <VStack>
          <Text size="small" color="white">
            {i18n.t('entity.integration.labels.evaClaimTooltipServiceRequests')}
          </Text>
          {orderData?.tooltip?.serviceRequests?.map((request, index) => (
            // eslint-disable-next-line react/no-array-index-key -- Pre-existing violation
            <Text size="small" color="white" key={index}>
              {DOT_CHARACTER} {request.title}
            </Text>
          ))}
        </VStack>
      </Show>
    </VStack>
  );
};
